import React, { useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';

// Composes a customer's watermarked preview photo into a published LookTemplate,
// entirely in the browser (display only — real product composition is the EC2
// spec's job). Layers, bottom to top: background, photo(s), overlay graphic,
// text fields. Geometry values are canvas-relative fractions (0..1), matching
// the admin Favorites Designer canvas; we render them as CSS percentages.
//
// Font sizing uses container-query height units (cqh) so text scales with the
// rendered canvas at any size: the wrapper is `container-type: size` (.sg-tpl)
// and `size_fraction` (font px / canvas px) maps to `size_fraction * 100` cqh.
const pct = (n) => `${(Number(n) || 0) * 100}%`;

const Photo = ({ url, backgroundUrl, fit }) => (
  <div
    className="sg-tpl-photo"
    style={{
      width: '100%',
      height: '100%',
      backgroundColor: '#e5e7eb',
      ...(backgroundUrl ? { backgroundImage: `url(${backgroundUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}),
    }}
  >
    {url && (
      <img
        src={url}
        alt=""
        style={{
          width: '100%', height: '100%', objectFit: fit === 'contain' ? 'contain' : 'cover', display: 'block',
        }}
      />
    )}
  </div>
);

Photo.propTypes = {
  url: PropTypes.string,
  backgroundUrl: PropTypes.string,
  fit: PropTypes.string,
};
Photo.defaultProps = { url: null, backgroundUrl: null, fit: 'cover' };

// Elegant cursive stack for "script" fields (no web-font load; degrades to cursive).
const SCRIPT_FONT = "'Snell Roundhand','Apple Chancery','Brush Script MT','Segoe Script',cursive";

// size_fraction is EC2's MAX font size (the auto-fit range top); EC2 then shrinks
// the text to fit its bound box. We do the same: render at the max, measure, and
// scale the text down uniformly so it fits the box width (never up-scale). Without
// this the max size overflows badly for longer names.
const TextField = ({ field, studentName }) => {
  const boxRef = useRef(null);
  const textRef = useRef(null);
  const isScript = field.font === 'script';
  const content = (field.content || '').replace(/\{\{\s*name\s*\}\}/gi, studentName || '');

  useLayoutEffect(() => {
    const box = boxRef.current;
    const text = textRef.current;
    if (!box || !text) return;
    text.style.transform = 'scale(1)';
    const boxW = box.clientWidth;
    const textW = text.scrollWidth;
    if (boxW > 0 && textW > boxW) text.style.transform = `scale(${boxW / textW})`;
  }, [content, field.size_fraction]);

  if (!content.trim()) return null; // e.g. a {{name}} field with no name

  // Confine to explicit bounds when set (a side column); else center on center_x.
  const bounded = field.bound_x_left != null && field.bound_x_right != null;
  const align = field.align || 'center';
  const justify = { left: 'flex-start', right: 'flex-end' }[align] || 'center';
  const origin = { left: 'left center', right: 'right center' }[align] || 'center center';
  const box = bounded
    ? {
      left: pct(field.bound_x_left),
      width: pct(field.bound_x_right - field.bound_x_left),
      transform: 'translateY(-50%)',
    }
    : { left: pct(field.center_x), width: '92%', transform: 'translate(-50%, -50%)' };

  return (
    <div
      ref={boxRef}
      style={{
        position: 'absolute',
        top: pct(field.center_y),
        ...box,
        display: 'flex',
        justifyContent: justify,
        pointerEvents: 'none',
      }}
    >
      <span
        ref={textRef}
        style={{
          whiteSpace: 'nowrap',
          transformOrigin: origin,
          color: field.color || '#000',
          fontFamily: isScript ? SCRIPT_FONT : undefined,
          fontWeight: (!isScript && field.bold) ? 800 : 500,
          fontStyle: field.italic ? 'italic' : 'normal',
          fontSize: field.size_fraction ? `${field.size_fraction * 100}cqh` : '5cqh',
          lineHeight: 1.05,
          letterSpacing: isScript ? 'normal' : '0.02em',
          textShadow: field.shadow ? '0 2px 6px rgba(0,0,0,.55)' : 'none',
        }}
      >
        {content}
      </span>
    </div>
  );
};

TextField.propTypes = {
  field: PropTypes.shape({
    content: PropTypes.string,
    color: PropTypes.string,
    center_x: PropTypes.number,
    center_y: PropTypes.number,
    align: PropTypes.string,
    bold: PropTypes.bool,
    italic: PropTypes.bool,
    shadow: PropTypes.bool,
    font: PropTypes.string,
    bound_x_left: PropTypes.number,
    bound_x_right: PropTypes.number,
    size_fraction: PropTypes.number,
  }).isRequired,
  studentName: PropTypes.string,
};
TextField.defaultProps = { studentName: null };

const TemplatePreview = ({
  template, photoUrl, backgroundUrl, studentName, style,
}) => {
  // No template selected: render the plain photo (caller controls the box).
  if (!template) {
    return <Photo url={photoUrl} backgroundUrl={backgroundUrl} />;
  }

  const aspect = `${template.canvas_width} / ${template.canvas_height}`;
  const isCollage = template.look_type === 'collage' && Array.isArray(template.slots);

  return (
    <div
      className="sg-tpl"
      style={{
        position: 'relative', width: '100%', aspectRatio: aspect, overflow: 'hidden', ...style,
      }}
    >
      {/* Solid card background (e.g. a graduation announcement's black card) */}
      {template.background_color && (
        <div style={{ position: 'absolute', inset: 0, background: template.background_color }} />
      )}

      {/* Background layer */}
      {template.canvas_url ? (
        <img
          src={template.canvas_url}
          alt=""
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
          }}
        />
      ) : (
        !isCollage && !template.background_color
          && <div style={{ position: 'absolute', inset: 0 }}><Photo url={photoUrl} backgroundUrl={backgroundUrl} /></div>
      )}

      {/* Collage member slots (a framed slot draws a thin double-line border) */}
      {isCollage && template.slots.map((slot, i) => (
        <div
          // Slots are positional and have no stable id; index is the identity here.
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          style={{
            position: 'absolute',
            left: pct(slot.x),
            top: pct(slot.y),
            width: pct(slot.width),
            height: pct(slot.height),
            transform: slot.rotation ? `rotate(${slot.rotation}deg)` : undefined,
          }}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              ...(slot.frame_color ? {
                border: `0.5cqh solid ${slot.frame_color}`,
                outline: `0.4cqh solid ${slot.frame_color}`,
                outlineOffset: '0.7cqh',
              } : {}),
            }}
          >
            <Photo url={photoUrl} backgroundUrl={backgroundUrl} fit={slot.fit_mode} />
          </div>
        </div>
      ))}

      {/* Overlay graphic (frame / memory-mate art) */}
      {template.overlay_url && (
        <img
          src={template.overlay_url}
          alt=""
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'fill', pointerEvents: 'none',
          }}
        />
      )}

      {/* Text fields */}
      {(template.text_fields || []).map((field, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <TextField key={i} field={field} studentName={studentName} />
      ))}
    </div>
  );
};

TemplatePreview.propTypes = {
  template: PropTypes.shape({
    look_type: PropTypes.string,
    canvas_width: PropTypes.number,
    canvas_height: PropTypes.number,
    background_color: PropTypes.string,
    overlay_url: PropTypes.string,
    canvas_url: PropTypes.string,
    slots: PropTypes.arrayOf(PropTypes.object),
    text_fields: PropTypes.arrayOf(PropTypes.object),
  }),
  photoUrl: PropTypes.string,
  backgroundUrl: PropTypes.string,
  studentName: PropTypes.string,
  style: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
};

TemplatePreview.defaultProps = {
  template: null,
  photoUrl: null,
  backgroundUrl: null,
  studentName: null,
  style: null,
};

export default TemplatePreview;
